import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { I18nService } from '../core/i18n.service';
import { PlayerAuthApiService } from '../core/player-auth-api.service';

@Component({
  selector: 'app-player-register-page',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  template: `
    <section class="page">
      <h1>{{ t('player.register.title') }}</h1>
      <p class="hint">{{ t('player.register.hint') }}</p>

      @if (successMessage()) {
        <p class="state state--success">{{ successMessage() }}</p>
      }
      @if (errorMessage()) {
        <p class="state state--error">{{ errorMessage() }}</p>
      }

      <form (ngSubmit)="submit()" #form="ngForm" class="form">
        <label>{{ t('player.register.email') }}
          <input type="email" name="email" required [(ngModel)]="email" />
        </label>
        <label>{{ t('player.register.pseudo') }}
          <input type="text" name="pseudo" required [(ngModel)]="pseudo" />
        </label>
        <label>{{ t('player.register.activision') }}
          <input type="text" name="activisionId" required [(ngModel)]="activisionId" />
        </label>
        <label>{{ t('player.register.password') }}
          <input type="password" name="password" required [(ngModel)]="password" />
        </label>
        <label>{{ t('player.register.passwordConfirm') }}
          <input type="password" name="passwordConfirmation" required [(ngModel)]="passwordConfirmation" />
        </label>

        <button type="submit" [disabled]="loading() || !form.valid">
          {{ loading() ? t('player.register.loading') : t('player.register.submit') }}
        </button>
      </form>

      <p><a routerLink="/player/login" [queryParams]="loginQueryParams()">{{ t('player.register.hasAccount') }}</a></p>
    </section>
  `,
  styles: `
    .page { padding: 1rem; max-width: 520px; }
    h1 { margin: 0 0 0.5rem; font-size: 1.4rem; }
    .hint { margin: 0 0 1rem; color: #52606d; }
    .form { display: grid; gap: 0.75rem; }
    label { display: grid; gap: 0.35rem; font-size: 0.92rem; }
    input { padding: 0.55rem 0.6rem; border: 1px solid #c9d2dc; border-radius: 8px; }
    button { border: 0; border-radius: 8px; padding: 0.6rem 0.8rem; background: #1d4c9a; color: #fff; font-weight: 600; }
    button:disabled { opacity: 0.7; }
    .state { margin: 0.4rem 0 0.75rem; }
    .state--error { color: #9d1c1c; }
    .state--success { color: #1b6a31; }
    a { color: #1d4c9a; text-decoration: none; }
  `
})
export class PlayerRegisterPageComponent {
  private readonly api = inject(PlayerAuthApiService);
  private readonly i18n = inject(I18nService);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);

  protected email = '';
  protected pseudo = '';
  protected activisionId = '';
  protected password = '';
  protected passwordConfirmation = '';

  protected readonly loading = signal(false);
  protected readonly successMessage = signal<string | null>(null);
  protected readonly errorMessage = signal<string | null>(null);

  protected t(key: string, params?: Record<string, string | number | null | undefined>): string {
    return this.i18n.t(key, params);
  }

  protected loginQueryParams(): { returnTo?: string } {
    const returnTo = this.returnToUrl();
    return returnTo ? { returnTo } : {};
  }

  protected submit(): void {
    if (this.password !== this.passwordConfirmation) {
      this.errorMessage.set('Les mots de passe ne correspondent pas.');
      return;
    }

    this.loading.set(true);
    this.successMessage.set(null);
    this.errorMessage.set(null);

    this.api
      .register({
        email: this.email.trim(),
        pseudo: this.pseudo.trim(),
        activision_id: this.activisionId.trim(),
        password: this.password,
        password_confirmation: this.passwordConfirmation
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.successMessage.set(response.message || 'Compte cree. Verifiez votre e-mail avant connexion.');
          this.loading.set(false);
        },
        error: (error) => {
          const apiErrors = error?.error?.errors;
          this.errorMessage.set(
            Array.isArray(apiErrors) && apiErrors.length > 0
              ? apiErrors.join(' | ')
              : 'Inscription impossible. Verifiez vos informations puis reessayez.'
          );
          this.loading.set(false);
        }
      });
  }

  private returnToUrl(): string | null {
    const returnTo = this.route.snapshot.queryParamMap.get('returnTo');
    if (!returnTo || !returnTo.startsWith('/')) {
      return null;
    }
    return returnTo;
  }
}
